import classNames from 'classnames';
import React, { Fragment, useEffect } from 'react';
import { useTimeIndicator } from 'features/meetings/hooks';
import { type Meeting, type MeetingGroupEvent, isEmptyMeetingEvent } from 'features/meetings/types';
import { useContainerBreakpoints } from 'hooks/use_container_breakpoints';
import { useResizeObserver } from 'hooks/use_resize_observer';
import { CurrentTimeIndicator } from './CurrentTimeIndicator';
import { DateBadge } from './DateBadge';
import { EmptyState } from './EmptyState';
import { MeetingCard } from '../../meeting_card';
import { MEETING_GROUP_BREAKPOINTS } from '../../meeting_card/utils';
const meetingGroupCompactCss = "mb9wxna";
const meetingGroupCss = "m7d9gqa";
const meetingsListCompactCss = "m1vxnfka";
const meetingsListCss = "m1q2mao8";
interface MeetingGroupProps {
  date: string;
  rawDate?: string;
  events: MeetingGroupEvent[];
  selectedMeetingId?: string;
  showBuffers?: boolean;
  anyInProgress?: boolean;
  packageNameMap?: Record<string, string>;
  onboardingTourMeetingRefCallback?: (el: HTMLElement | null) => void;
  onboardingTourNotetakerRefCallback?: (el: HTMLElement | null) => void;
  firstUpcomingEventKey?: string;
  registerDateGroupRef?: (date: string, el: HTMLElement | null) => void;
}

/**
 * Renders a group of meetings for a single date, including the date badge,
 * meeting cards, and current time indicator. On desktop, the date badge
 * is displayed alongside the meetings list; on mobile, it stacks above
 * with sticky positioning.
 *
 * @param date - Formatted display date string shown in the DateBadge
 * @param rawDate - Optional raw ISO date string used for scroll anchoring and time indicator comparisons
 * @param events - Ordered list of meeting events (or empty state placeholders) for this date
 * @param selectedMeetingId - UUID of the currently selected meeting, used to highlight the card
 * @param showBuffers - Whether to render buffer time indicators on meeting cards
 * @param anyInProgress - Whether any meeting in the list is currently in progress
 * @param packageNameMap - Map of meeting UUID to package name for package booking display
 * @param onboardingTourMeetingRefCallback - Ref callback for attaching the onboarding tour anchor to a meeting card
 * @param onboardingTourNotetakerRefCallback - Ref callback for attaching the onboarding tour anchor to a notetaker control
 * @param firstUpcomingEventKey - Key of the first upcoming event, used to identify the tour target card
 * @param registerDateGroupRef - Callback to register this group's DOM element by date for scroll-to-date navigation
 */
export const MeetingGroup = ({
  date,
  rawDate,
  events,
  selectedMeetingId,
  showBuffers,
  anyInProgress,
  packageNameMap = {},
  onboardingTourMeetingRefCallback,
  onboardingTourNotetakerRefCallback,
  firstUpcomingEventKey,
  registerDateGroupRef
}: MeetingGroupProps) => {
  const {
    ref: groupRef,
    width
  } = useResizeObserver<HTMLDivElement>();
  const {
    compact: isCompact
  } = useContainerBreakpoints(width, MEETING_GROUP_BREAKPOINTS);
  const firstEvent = events[0];
  const firstEventDate = firstEvent && !isEmptyMeetingEvent(firstEvent) ? firstEvent.date : undefined;
  const comparableDate = rawDate || firstEventDate || date;
  const dateForComparison = rawDate || firstEventDate || null;
  const {
    indicatorIndex,
    mostRecentInProgressIndex,
    showIndicatorAfterAll,
    hasRealtimeFeatures
  } = useTimeIndicator(comparableDate, events);
  useEffect(() => {
    if (registerDateGroupRef && dateForComparison) {
      registerDateGroupRef(dateForComparison, groupRef.current);
      return () => {
        registerDateGroupRef(dateForComparison, null);
      };
    }
    return undefined;
  }, [registerDateGroupRef, dateForComparison, groupRef]);
  return <div ref={groupRef} data-date={dateForComparison} className={classNames(meetingGroupCss, {
    [meetingGroupCompactCss]: isCompact
  })}>
      <DateBadge date={rawDate || date} isCompact={isCompact} />
      <div className={classNames(meetingsListCss, {
      [meetingsListCompactCss]: isCompact
    })}>
        {events.map((event, index, list) => {
        const {
          key
        } = event;
        const isLast = index === list.length - 1;
        const isFirst = index === 0;
        const showIndicatorBefore = index === indicatorIndex;
        const isMostRecentInProgress = index === mostRecentInProgressIndex;
        return <Fragment key={key}>
              {showIndicatorBefore && <CurrentTimeIndicator isRealtime={hasRealtimeFeatures} />}
              {isEmptyMeetingEvent(event) ? <EmptyState type={event.type} /> : <MeetingCard anyInProgress={anyInProgress} event={event} isFirst={isFirst} isLast={isLast} isMostRecentInProgress={isMostRecentInProgress} isNext={showIndicatorBefore} otherEventsOnDay={list.filter((e): e is Meeting => !isEmptyMeetingEvent(e) && e.uuid !== event.uuid)} selectedEventId={selectedMeetingId} showBuffers={showBuffers} packageName={packageNameMap[event.uuid]} onboardingTourMeetingRefCallback={onboardingTourMeetingRefCallback} onboardingTourNotetakerRefCallback={onboardingTourNotetakerRefCallback} isFirstUpcomingForTour={(event.key ?? event.uuid) === firstUpcomingEventKey} />}
            </Fragment>;
      })}
        {showIndicatorAfterAll && <CurrentTimeIndicator isRealtime={hasRealtimeFeatures} />}
      </div>
    </div>;
};

require("./MeetingGroup.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./MeetingGroup.tsx");